// Mes Avis Artisans/Prestataires
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Edit, Trash2, ThumbsUp, MessageCircle, X, Loader2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEMO_AVIS = [
  {
    id: 'avis-1',
    boutique_name: 'Plomberie Durand & Fils',
    specialty: 'Plombier',
    rating: 5,
    comment: 'Intervention rapide et efficace pour une fuite sous évier. Très professionnel, propre et tarifé correctement. Je recommande vivement !',
    created_at: '2025-01-28T10:00:00Z',
    helpful_count: 12,
    vendor_response: 'Merci beaucoup pour votre confiance ! N\'hésitez pas à nous recontacter.',
  },
  {
    id: 'avis-2',
    boutique_name: 'Léonard Martin Menuiserie',
    specialty: 'Menuisier',
    product_name: 'Pose parquet chêne 45m²',
    rating: 4,
    comment: 'Beau travail pour la pose du parquet, finitions soignées. Légèrement en retard sur le planning initial mais le résultat est impeccable.',
    created_at: '2025-02-15T14:00:00Z',
    helpful_count: 7,
    vendor_response: null,
  },
];

const DEMO_PENDING = [
  {
    boutique_slug: 'electricite-pro-martin',
    boutique_name: 'Électricité Pro Martin',
    specialty: 'Électricien',
    service: 'Installation tableau électrique',
  },
];

function getToken() {
  return localStorage.getItem('ecosystem_token') || localStorage.getItem('social_token') || '';
}

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

function AvisCard({ avis, onEdit, onDelete }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:border-orange-200 hover:shadow-sm transition-all"
    >
      <div className="flex gap-4">
        <div className="w-14 h-14 bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
          {(avis.boutique_name || avis.artisanName || '?').charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {avis.boutique_name || avis.artisanName}
          </h3>
          {avis.specialty && <p className="text-xs text-orange-600 font-medium">{avis.specialty}</p>}
          {avis.product_name && <p className="text-xs text-gray-500">{avis.product_name}</p>}
          <div className="flex items-center gap-1 mt-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={15}
                className={i < avis.rating ? 'text-amber-500 fill-amber-500' : 'text-gray-200'} />
            ))}
            <span className="text-xs text-gray-400 ml-2">{formatDate(avis.created_at || avis.date)}</span>
          </div>
        </div>
      </div>

      <p className="mt-3 text-sm text-gray-700">{avis.comment}</p>

      {(avis.vendor_response || avis.artisanResponse) && (
        <div className="mt-3 p-3 bg-[#1E3A5F]/5 rounded-xl border-l-4 border-[#1E3A5F]/30">
          <p className="text-xs font-semibold text-[#1E3A5F] mb-1">Réponse du prestataire</p>
          <p className="text-sm text-gray-700">{avis.vendor_response || avis.artisanResponse}</p>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
        <span className="flex items-center gap-1 text-xs text-gray-400">
          <ThumbsUp size={12} />
          {avis.helpful_count ?? 0} utile{(avis.helpful_count ?? 0) > 1 ? 's' : ''}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(avis)}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-[#1E3A5F] hover:bg-gray-50 rounded-lg transition-colors"
          >
            <Edit size={12} /> Modifier
          </button>
          <button
            onClick={() => onDelete(avis)}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 size={12} /> Supprimer
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function AvisArtisans() {
  const [avis, setAvis] = useState(DEMO_AVIS);
  const [pending, setPending] = useState(DEMO_PENDING);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Modal donner un avis
  const [showAvisModal, setShowAvisModal] = useState(false);
  const [selectedPrestataire, setSelectedPrestataire] = useState(null);
  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [hoverRating, setHoverRating] = useState(0);

  // Modal modifier
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAvis, setEditingAvis] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editComment, setEditComment] = useState('');
  const [editHover, setEditHover] = useState(0);

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    setError(null);
    try {
      const [sentRes, pendingRes] = await Promise.all([
        fetch(`${API_URL}/api/boutique/me/avis-envoyes`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/boutique/me/pending-reviews`, { headers: authHeaders() }),
      ]);

      if (sentRes.ok) {
        const data = await sentRes.json();
        const real = data.reviews || [];
        if (real.length > 0) {
          setAvis(real);
          setIsDemo(false);
        }
      }
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        const realPending = data.pending || [];
        if (realPending.length > 0) {
          setPending(realPending);
          setIsDemo(false);
        }
      }
    } catch {
      // keep demo data on error
    } finally {
      setLoading(false);
    }
  }

  function openAvisModal(prestataire) {
    setSelectedPrestataire(prestataire);
    setNewRating(0);
    setNewComment('');
    setShowAvisModal(true);
  }

  async function handleSubmitAvis() {
    if (!newRating || !newComment.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/boutique/${selectedPrestataire.boutique_slug}/avis`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            rating: newRating,
            comment: newComment.trim(),
            review_type: 'artisan',
            product_name: selectedPrestataire.service || null,
          }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        setAvis(prev => [data.review, ...prev]);
        setPending(prev => prev.filter(p => p.boutique_slug !== selectedPrestataire.boutique_slug));
        setShowAvisModal(false);
      } else {
        const err = await res.json();
        setError(err.detail || 'Erreur lors de la publication.');
      }
    } catch {
      setError('Erreur de connexion.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleEdit(avisItem) {
    setEditingAvis(avisItem);
    setEditRating(avisItem.rating);
    setEditComment(avisItem.comment);
    setShowEditModal(true);
  }

  async function handleSaveEdit() {
    if (!editRating || !editComment.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/boutique/${editingAvis.boutique_slug}/avis/${editingAvis.id}`,
        {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ rating: editRating, comment: editComment.trim() }),
        }
      );
      if (res.ok) {
        setAvis(prev => prev.map(a =>
          a.id === editingAvis.id ? { ...a, rating: editRating, comment: editComment.trim() } : a
        ));
        setShowEditModal(false);
      } else {
        const err = await res.json();
        setError(err.detail || 'Erreur lors de la modification.');
      }
    } catch {
      setError('Erreur de connexion.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(avisItem) {
    if (!window.confirm('Supprimer cet avis définitivement ?')) return;
    try {
      const res = await fetch(
        `${API_URL}/api/boutique/${avisItem.boutique_slug}/avis/${avisItem.id}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      if (res.ok) {
        setAvis(prev => prev.filter(a => a.id !== avisItem.id));
      }
    } catch {
      setError('Erreur lors de la suppression.');
    }
  }

  const avgRating = avis.length > 0
    ? avis.reduce((acc, a) => acc + a.rating, 0) / avis.length
    : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Avis</h1>
          <p className="text-xs sm:text-sm text-gray-500">
            {loading ? '…' : `${avis.length} avis laissé${avis.length > 1 ? 's' : ''}`}
          </p>
        </div>
        {isDemo && (
          <span className="px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-lg text-xs font-medium text-amber-700">
            Aperçu démo
          </span>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-orange-500" size={28} />
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Star size={24} className="text-amber-500 fill-amber-500" />
                  <span className="text-2xl sm:text-3xl font-bold text-gray-900">{avgRating.toFixed(1)}</span>
                </div>
                <p className="text-xs sm:text-sm text-gray-500">Note moyenne</p>
              </div>
              <div className="flex-1">
                <div className="space-y-1">
                  {[5, 4, 3, 2, 1].map(star => {
                    const count = avis.filter(a => a.rating === star).length;
                    const percent = avis.length > 0 ? (count / avis.length) * 100 : 0;
                    return (
                      <div key={star} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-3">{star}</span>
                        <Star size={11} className="text-amber-500 fill-amber-500" />
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-500 rounded-full transition-all"
                            style={{ width: `${percent}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 w-4">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Avis en attente */}
          {pending.length > 0 && (
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
              <h3 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <MessageCircle size={16} />
                Avis en attente ({pending.length})
              </h3>
              <div className="space-y-3">
                {pending.map(p => (
                  <div key={p.boutique_slug} className="flex items-center gap-3 bg-white rounded-xl p-3">
                    <div className="w-11 h-11 bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0">
                      {p.boutique_name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.boutique_name}</p>
                      {p.specialty && <p className="text-xs text-orange-600 font-medium">{p.specialty}</p>}
                      {p.service && <p className="text-xs text-gray-500">{p.service}</p>}
                    </div>
                    <button
                      onClick={() => openAvisModal(p)}
                      className="flex-shrink-0 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-xl transition-colors"
                    >
                      ⭐ Donner mon avis
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Liste des avis */}
          {avis.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Star size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun avis</h3>
              <p className="text-gray-500 text-sm">Tu n'as pas encore laissé d'avis sur tes prestataires.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {avis.map(a => (
                <AvisCard key={a.id} avis={a} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal Donner un avis */}
      {createPortal(
        <AnimatePresence>
          {showAvisModal && selectedPrestataire && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/60 p-4"
              onClick={() => setShowAvisModal(false)}>
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <h3 className="text-base font-bold text-gray-900">Donner mon avis</h3>
                  <button onClick={() => setShowAvisModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                    <X size={18} className="text-gray-500" />
                  </button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0">
                      {selectedPrestataire.boutique_name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{selectedPrestataire.boutique_name}</p>
                      {selectedPrestataire.specialty && <p className="text-xs text-orange-600">{selectedPrestataire.specialty}</p>}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Ta note</p>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button key={star}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => setNewRating(star)}
                          className="p-1 transition-transform hover:scale-110">
                          <Star size={32} className={`${star <= (hoverRating || newRating) ? 'text-amber-500 fill-amber-500' : 'text-gray-200'} transition-colors`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Ton commentaire</p>
                    <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
                      placeholder="Décris ton expérience avec ce prestataire..."
                      className="w-full p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 text-[16px] bg-gray-50"
                      rows={4} />
                  </div>
                </div>
                <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
                  <button
                    onClick={() => setShowAvisModal(false)}
                    className="flex-1 h-12 rounded-xl border border-gray-300 text-[#1e3a5f] text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSubmitAvis}
                    disabled={!newRating || !newComment.trim() || submitting}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    Publier mon avis
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Modal Modifier */}
      {createPortal(
        <AnimatePresence>
          {showEditModal && editingAvis && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/60 p-4"
              onClick={() => setShowEditModal(false)}>
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <h3 className="text-base font-bold text-gray-900">Modifier mon avis</h3>
                  <button onClick={() => setShowEditModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                    <X size={18} className="text-gray-500" />
                  </button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0">
                      {(editingAvis.boutique_name || '?').charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{editingAvis.boutique_name}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Ta note</p>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button key={star}
                          onMouseEnter={() => setEditHover(star)}
                          onMouseLeave={() => setEditHover(0)}
                          onClick={() => setEditRating(star)}
                          className="p-1 transition-transform hover:scale-110">
                          <Star size={32} className={`${star <= (editHover || editRating) ? 'text-amber-500 fill-amber-500' : 'text-gray-200'} transition-colors`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Ton commentaire</p>
                    <textarea value={editComment} onChange={e => setEditComment(e.target.value)}
                      className="w-full p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 text-[16px] bg-gray-50"
                      rows={4} />
                  </div>
                </div>
                <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 h-12 rounded-xl border border-gray-300 text-[#1e3a5f] text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={!editRating || !editComment.trim() || submitting}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    Enregistrer
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
